// Utility function to clean up Rebuy modal classes
function cleanupRebuyModalClasses() {
  setTimeout(() => {
    if (document.body.classList.contains('rebuy-modal-visible') ||
        document.body.classList.contains('rebuy-cart-visible')) {
      document.body.classList.remove('rebuy-modal-visible', 'rebuy-cart-visible');
    }
  }, 100);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Listen for ESC key on the entire cart drawer element
    this.addEventListener('keydown', (evt) => {
      if (evt.code === 'Escape') {
        evt.preventDefault();
        this.close();
      }
    });

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initializeGiftWrap();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-drawer') {
        return;
      }

      // For product-form events, we need to fetch the cart sections
      // since the response might not have the sections we need
      if (event.source === 'product-form' && event.cartData) {
        this.fetchCartSections(event.cartData);
      } else if (event.cartData) {
        this.renderContents(event.cartData);
      }
    });
  }

  fetchCartSections(cartData) {
    const sectionsToRender = this.getSectionsToRender().map((section) => section.id);

    fetch(`${routes.cart_url}?sections=${sectionsToRender.join(',')}`)
        .then((response) => response.json())
        .then((data) => {
          const parsedState = {
            ...cartData,
            sections: data
          };

          // Update the cart icon separately since it's not a section
          this.updateCartIcon(cartData);

          // Render contents but skip auto open (we'll handle it manually)
          this.renderContents(parsedState, true);

          // Force open the drawer since a product was added
          setTimeout(() => {
            this.open();
          }, 100);
        })
        .catch((error) => {
          console.error('Error fetching cart sections:', error);
          // Update cart icon anyway with the cart data we have
          this.updateCartIcon(cartData);
          // Fallback: just open the drawer without rendering
          this.open();
        });
  }

  updateCartIcon(cartData) {
    // Update the cart icon bubble with current cart data
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon && cartData) {
      // Extract cart item count from cartData
      const itemCount = cartData.item_count || (cartData.items ? cartData.items.length : 0);

      // Update the cart count display
      const countElements = cartIcon.querySelectorAll('.menu-item-count');
      countElements.forEach(element => {
        if (element.classList.contains('menu-item-count-desk')) {
          element.textContent = `(${itemCount})`;
        } else if (element.classList.contains('menu-item-count-mob')) {
          element.textContent = itemCount;
        }
      });

      // Show/hide the count bubble based on whether cart is empty
      const countBubble = cartIcon.querySelector('.cart-count-bubble');
      if (countBubble) {
        if (itemCount > 0) {
          countBubble.style.display = '';
        } else {
          countBubble.style.display = 'none';
        }
      }
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, showFocus = true) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Store current scroll position
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Calculate scrollbar width and prevent body scroll while keeping scrollbar visible
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // Check gift wrap checkbox state when opening cart drawer
    this.checkGiftWrapOnOpen();

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
              ? this.querySelector('.drawer__inner')
              : this.querySelector('.drawer__inner');

          // Only show focus on close button if explicitly requested (e.g., user-initiated)
          const focusElement = showFocus ? this.querySelector('.drawer__close') : null;
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
    );
    document.body.classList.add('overflow-hidden');
  }

  isGiftCardItem(item) {
    const t = (item.product_type || '').trim().toLowerCase();
    return t === 'gift card' || t === 'gift cards' || t === 'giftcard' || t === 'gift-card';
  }

  async checkGiftWrapOnOpen() {
    try {
      // Fetch current cart data
      const response = await fetch(`${routes.cart_url}.js`);
      const cartData = await response.json();

      // Check for gift wrap product that should be removed if alone
      this.checkGiftWrapProduct(cartData.items || []);

      // Create a mock parsedState object similar to what updateGiftWrapCheckbox expects
      const mockParsedState = {
        items: cartData.items || []
      };

      // Run the gift wrap checkbox check
      this.updateGiftWrapCheckbox(mockParsedState);
    } catch (error) {
      console.error('Error checking gift wrap on cart drawer open:', error);
    }
  }

  checkGiftWrapProduct(items) {
    // Znajdź input z danymi gift wrapu
    const giftWrapInput = this.querySelector('.gift-wrap-checkbox[data-product-id][data-variant-id]');
    if (!giftWrapInput) return;

    const giftWrapProductId = giftWrapInput.dataset.productId;
    const giftWrapVariantId = giftWrapInput.dataset.variantId;
    if (!giftWrapProductId || !giftWrapVariantId) return;

    // Items gift wrapu
    const giftWrapItems = items.filter(it => it.product_id && it.product_id.toString() === giftWrapProductId.toString());
    if (giftWrapItems.length === 0) return; // brak w koszyku — nie ma czego usuwać

    // Items inne niż gift wrap
    const nonGiftWrapItems = items.filter(it => !it.product_id || it.product_id.toString() !== giftWrapProductId.toString());

    // Warunki:
    const onlyGiftWrap = nonGiftWrapItems.length === 0; // (A) gift wrap jedyny
    const onlyGiftCards = nonGiftWrapItems.length > 0 && nonGiftWrapItems.every(it => this.isGiftCardItem(it)); // (B) poza GW — same gift cards

    if (onlyGiftWrap || onlyGiftCards) {
      // usuń JEDEN wpis gift wrapu (jeżeli jest kilka, usuń pierwszy – zwykle i tak qty=1)
      this.removeGiftWrapProduct(giftWrapItems[0]);
    }
  }

  async removeGiftWrapProduct(giftWrapItem) {
    try {
      const updates = { [giftWrapItem.key]: 0 };

      const response = await fetch(`${routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        console.error('Failed to remove gift wrap product:', response.status, await response.text());
        return;
      }

      const cartData = await response.json();
      this.updateCartSectionsOnly(cartData);
    } catch (error) {
      console.error('Error removing gift wrap product:', error);
    }
  }

  async removeGiftWrapFromCart(variantId) {
    try {
      const currentScrollPosition = this.scrollPosition;
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();

      const giftWrapItem = cart.items.find(i => i.variant_id.toString() === variantId.toString());
      if (!giftWrapItem) return;

      const updates = { [giftWrapItem.key]: 0 };

      const response = await fetch(`${routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) throw new Error('Failed to remove gift wrap from cart');

      const cartData = await response.json();
      this.scrollPosition = currentScrollPosition;
      this.updateCartSectionsOnly(cartData);
      cleanupRebuyModalClasses();
    } catch (error) {
      console.error('Error in removeGiftWrapFromCart:', error);
      throw error;
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';


    // Restore scroll position
    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
    }

    // Restore focus to the element that opened the cart
    if (this.activeElement) {
      this.activeElement.focus();
    }

    // Clean up Rebuy modal classes after closing
    cleanupRebuyModalClasses();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, skipAutoOpen = false) {
    const wasEmpty = this.classList.contains('is-empty');
    const isNowEmpty = !parsedState.items || parsedState.items.length === 0;

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
    this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    if (parsedState.sections) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

        if (!sectionElement) return;
        if (parsedState.sections[section.id]) {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      });
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

      // Update gift wrap checkbox state
      this.updateGiftWrapCheckbox(parsedState);

      // Only handle automatic open/close if not skipping auto open
      if (!skipAutoOpen) {
        // Close if cart is now empty (item was removed)
        // Open if cart has items (item was added) but only if not already open
        if (isNowEmpty) {
          this.close();
        } else if (!this.classList.contains('active')) {
          this.open();
        }
      }
    });
  }

  reestablishFocusTrap() {
    // Check if cart is currently active and re-establish focus trap
    if (this.classList.contains('active')) {
      const containerToTrapFocusOn = this.querySelector('.drawer__inner');
      const focusElement = this.querySelector('.drawer__close');

      // Remove existing trap and establish new one
      removeTrapFocus();
      trapFocus(containerToTrapFocusOn, focusElement);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeGiftWrap() {
    this.setupGiftWrapListener();
  }

  setupGiftWrapListener() {
    // Use event delegation to handle dynamically loaded content
    this.addEventListener('change', (event) => {
      if (event.target.classList.contains('gift-wrap-checkbox')) {
        this.handleGiftWrapToggle(event.target);
      }
    });
  }

  async handleGiftWrapToggle(checkbox) {
    const productId = checkbox.dataset.productId;
    const variantId = checkbox.dataset.variantId;
    const isChecked = checkbox.checked;

    // Store scroll position at the beginning
    const originalScrollPosition = this.scrollPosition;

    try {
      if (isChecked) {
        // Check if gift wrap is already in cart
        const isGiftWrapInCart = await this.isGiftWrapInCart(variantId);
        if (!isGiftWrapInCart) {
          await this.addGiftWrapToCart(variantId);
        }
      } else {
        // Remove gift wrap from cart
        await this.removeGiftWrapFromCart(variantId);
      }
    } catch (error) {
      console.error('Error handling gift wrap toggle:', error);
      // Reset checkbox state if there was an error
      checkbox.checked = !isChecked;
      // Ensure scroll position is preserved even on error
      this.scrollPosition = originalScrollPosition;
    }
  }

  async isGiftWrapInCart(variantId) {
    const response = await fetch(`${routes.cart_url}.js`);
    const cart = await response.json();
    return cart.items.some(item => item.variant_id.toString() === variantId.toString());
  }

  async addGiftWrapToCart(variantId) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const response = await fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      body: formData
    });

    if (response.ok) {
      const cartData = await response.json();

      // Update cart sections without forcing drawer to open
      this.updateCartSectionsOnly(cartData);

      // Clean up Rebuy modal classes after adding
      cleanupRebuyModalClasses();
    } else {
      throw new Error('Failed to add gift wrap to cart');
    }
  }

  updateGiftWrapCheckbox(parsedState) {
    const items = parsedState.items || [];
    
    // Check gift wrap product on every cart update
    this.checkGiftWrapProduct(items);

    const giftWrapSection = this.querySelector('.drawer__gift-wrap-section');
    if (!giftWrapSection) return;

    const checkbox = giftWrapSection.querySelector('.gift-wrap-checkbox');
    if (!checkbox) return;

    const variantId = checkbox.dataset.variantId;

    // Check if gift wrap is in cart
    const isGiftWrapInCart = items.some(item =>
        item.variant_id && item.variant_id.toString() === variantId.toString()
    );

    // Check if cart only contains Gift Card products (excluding gift wrap)
    const nonGiftWrapItems = items.filter(item =>
        item.variant_id.toString() !== variantId.toString()
    );

    const hasOnlyGiftCards = nonGiftWrapItems.length > 0 && nonGiftWrapItems.every(item => {
      const productType = item.product_type || '';
      return productType.toLowerCase() === 'gift card' ||
          productType.toLowerCase() === 'gift cards' ||
          productType.toLowerCase() === 'giftcard' ||
          productType.toLowerCase() === 'gift-card';
    });

    // If cart only has gift cards, remove gift wrap if it exists
    if (hasOnlyGiftCards && isGiftWrapInCart) {
      this.removeGiftWrapFromCart(variantId);
      return; // Exit early since gift wrap will be removed
    }

    // Hide the entire section if gift wrap is in cart OR if cart only has gift cards
    if (isGiftWrapInCart || hasOnlyGiftCards) {
      giftWrapSection.style.display = 'none';
    } else {
      giftWrapSection.style.display = 'block';
      checkbox.checked = false;
    }
  }

  async removeGiftWrapFromCart(variantId) {
    try {
      // Store scroll position before removal
      const currentScrollPosition = this.scrollPosition;

      // First get current cart to find the line item
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();

      const giftWrapItem = cart.items.find(item => item.variant_id.toString() === variantId.toString());
      if (!giftWrapItem) {
        return;
      }

      const updates = {};
      updates[giftWrapItem.key] = 0;

      const formData = new FormData();
      formData.append('updates', JSON.stringify(updates));

      const response = await fetch(`${routes.cart_update_url}.js`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const cartData = await response.json();

        // Ensure scroll position is preserved
        this.scrollPosition = currentScrollPosition;

        // Update cart sections without forcing drawer to open
        this.updateCartSectionsOnly(cartData);

        // Clean up Rebuy modal classes after removing
        cleanupRebuyModalClasses();
      } else {
        throw new Error('Failed to remove gift wrap from cart');
      }
    } catch (error) {
      console.error('Error in removeGiftWrapFromCart:', error);
      throw error;
    }
  }

  updateCartSectionsOnly(cartData) {
    // Store current scroll position before updating
    const currentScrollPosition = this.scrollPosition;

    const sectionsToRender = this.getSectionsToRender().map((section) => section.id);

    fetch(`${routes.cart_url}?sections=${sectionsToRender.join(',')}`)
        .then((response) => response.json())
        .then((data) => {
          const parsedState = {
            ...cartData,
            sections: data
          };

          // Update the cart icon separately since it's not a section
          this.updateCartIcon(cartData);

          // Restore the scroll position after updating
          this.scrollPosition = currentScrollPosition;

          // Render contents with skip auto open flag - no forced opening
          this.renderContents(parsedState, true);
        })
        .catch((error) => {
          console.error('Error updating cart sections:', error);
          // Update cart icon anyway with the cart data we have
          this.updateCartIcon(cartData);
          // Restore the scroll position even on error
          this.scrollPosition = currentScrollPosition;
        });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);